import axios, { isCancel, CancelToken } from 'axios'
import { serverData, getEuatToken, getEcToken } from './server-data'
import { FetchError } from './error'
import { getCmid } from './fraudnet'
import { errorTypes, FPTI_CONTEXT_ID_HEADER, REQUEST_HEADERS } from 'universal'
import { get as safeGet } from 'lodash-es'

const X_PAYPAL_INTERNAL_EUAT = 'x-paypal-internal-euat'

const {
  REQUEST_CANCELLED_ERROR,
  REQUEST_CONNECTION_ERROR,
  UNEXPECTED_RUNTIME_ERROR,
  UNEXPECTED_SERVER_ERROR,
  UNAUTHORIZED_ERROR,
  FORBIDDEN_ERROR,
  INTERNAL_SERVER_ERROR,
  REQUEST_BLOCKED_ERROR,
} = errorTypes

// this is only safe because all our axios calls are same-origin.
// NOTE: if ever using this file for cross-origin calls via axios, remove this header!
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

async function request(url, options = {}) {
  // Create cancel token
  const { cancelTokens, headers = {}, ...requestOptions } = options
  let cancelToken
  if (cancelTokens) {
    const tokenSource = CancelToken.source()
    cancelTokens.push(tokenSource)
    cancelToken = tokenSource.token
  }

  const clientMetadataId = getCmid() || serverData.clientMetadataId
  const euatToken = getEuatToken()
  const ecToken = getEcToken()
  const nsid = serverData.nsid

  // Trigger the network request
  let res
  let data
  try {
    res = await axios(
      Object.assign(
        { 'Content-Type': 'application/json' },
        {
          url,
          cancelToken,
          headers: {
            ...headers,
            ...(clientMetadataId && {
              [REQUEST_HEADERS.PAYPAL_CLIENT_METADATA_ID]: clientMetadataId,
            }),
            ...(euatToken && {
              [X_PAYPAL_INTERNAL_EUAT]: euatToken,
            }),
            ...(nsid && {
              'PayPal-Nsid': nsid,
            }),
            ...(ecToken && {
              'ec-token': ecToken,
            }),
          },
          ...requestOptions,
        },
      ),
    )

    const resData = res.data || {}
    data = resData.data

    // Create a reference to sysData
    if (resData.sys && data) {
      data.sysData = resData.sys
    }

    if (resData.error) {
      throw res
    }
  } catch (err) {
    handleError(err)
  }

  return data
}

function handleError(obj) {
  let res, err

  if (obj.data) {
    res = obj
  } else {
    err = obj
    // Runtime error, when there is not request config returned by axios
    if (!err.config) {
      // XHR request cancelled
      if (isCancel(err)) {
        throw new FetchError({ type: REQUEST_CANCELLED_ERROR, causedBy: err })
      }

      // eslint-disable-next-line no-console
      console.error('[ucs] Unexpected fetch runtime error', err)
      // So loading sign can be reset
      throw new FetchError({ type: UNEXPECTED_RUNTIME_ERROR, causedBy: err })
    }
    res = err.response
  }

  // No response, if connection is blocked by browser
  if (!res) {
    throw new FetchError({
      type: REQUEST_CONNECTION_ERROR,
      message: 'No response from the server',
      causedBy: err,
    })
  }

  // Set error type based on response status code or
  // type returned in response data
  const errorRes = res.data && (res.data.error || {})

  if (errorRes.type) {
    throw new FetchError(errorRes)
  } else {
    handleErrorByStatusCode(res.status, errorRes)
  }
}

/**
 * Fallback error handler by checking the status code, assuming
 * returned by paypalize middlewares.
 *
 * Throws unexpected server errror if
 */
function handleErrorByStatusCode(status, errorRes) {
  switch (status) {
    case 401:
      // User not logged in
      throw new FetchError({ type: UNAUTHORIZED_ERROR, ...errorRes })

    case 403:
      // CSRF token error
      throw new FetchError({ type: FORBIDDEN_ERROR, ...errorRes })

    // Rate limited, request blocked
    case 429:
      throw new FetchError({ type: REQUEST_BLOCKED_ERROR, ...errorRes })

    case 500:
      // Server runtime error
      throw new FetchError({ type: INTERNAL_SERVER_ERROR, ...errorRes })

    case 502:
      // Server is down
      throw new FetchError({
        type: REQUEST_CONNECTION_ERROR,
        message: 'Bad Gateway',
        ...errorRes,
      })

    default:
      const message = `API response error type is missing, status ${status}.`
      // eslint-disable-next-line no-console
      console.error(`[ucs] ${message}, response:`, errorRes)
      throw new FetchError({
        type: UNEXPECTED_SERVER_ERROR,
        message,
        ...errorRes,
      })
  }
}

async function get(url, options) {
  return await request(
    url,
    Object.assign({}, options, {
      method: 'get',
    }),
  )
}

async function post(url, options) {
  const csrf = serverData.csrf
  const contextId = safeGet(options, 'fptiHeader.contextId')
  return await request(url, {
    ...options,
    method: 'post',
    headers: {
      'X-CSRF-Token': csrf,
      ...(contextId && { [FPTI_CONTEXT_ID_HEADER]: contextId }),
    },
  })
}

async function patch(url, options) {
  const csrf = serverData.csrf
  return await request(url, {
    ...options,
    method: 'patch',
    headers: {
      'X-CSRF-Token': csrf
    },
  })
}

export { request, get, post, patch }
